/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import { PAMediaBatch } from "../../mediaRemoval/mediaRemoval.types";

interface Props {
	mediaBatches: PAMediaBatch[];
	selectedBatch?: PAMediaBatch;
	setSelectedBatch: (batch: PAMediaBatch) => void;
	errors: any;
    setErrors: React.Dispatch<React.SetStateAction<any>>;
	batchInfo: {
		id?: string;
		mediaName?: string;
		activityName: string;
		totalPlates: number;
	};
}
const PIHeader = ({
	batchInfo,
	mediaBatches,
	selectedBatch,
	setSelectedBatch,
	errors,
	setErrors
}: Props) => {
	const { t } = useTranslation();
	const handleBatchChange = (batchId: string) => {
		const batch = mediaBatches.find((b) => b.id === batchId);
		if (batch) {
			setSelectedBatch(batch);
			setErrors((prev: any) => ({
				...prev,
				batch: "",
			}));
		}
	};
	return (
		<div className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm shrink-0">
			<div className="px-8 py-4">
				<div className="flex justify-between items-center mb-4">
					<div className="flex items-center gap-5">
						<div>
							<h2 className="text-xl font-black text-[#1E293B] tracking-tight">
								{t("postAutoclaveProcess.physicalInspection.title")}
							</h2>
							<p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
								{t("postAutoclaveProcess.physicalInspection.subText")}
							</p>
						</div>
					</div>

					<div className="flex items-center gap-6">
						<div className="flex flex-col">
							<label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">
								{t("postAutoclaveProcess.physicalInspection.selectAMediaBatch")}  <span className="text-red-500">*</span>
							</label>
							<div className="relative group">
								<select
									value={selectedBatch?.id ?? ""}
									onChange={(e) => handleBatchChange(e.target.value)}
									className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[12px] text-slate-700 outline-none focus:border-[#FF7043] transition-all"
								>
									<option value="" disabled>
										{t("postAutoclaveProcess.physicalInspection.selectAMediaBatch")}
									</option>

									{mediaBatches.map((batch) => (
										<option key={batch.id} value={batch.id}>
											{batch.media_batch_identifier} — {batch.media_batch_description}
										</option>
									))}
								</select>
								{errors.batch && (
									<p className="mt-1 text-[11px] text-red-500">
										{errors.batch}
									</p>
								)}
							</div>
						</div>
						<div className="flex items-center gap-3">
							<div className="px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center gap-2 mt-4">
								<div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></div>
								<span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">
									{t(
										"postAutoclaveProcess.physicalInspection.inspectionActive",
									)}
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Batch Info Bar */}
				<div className="flex items-center gap-4">
					<div className="flex-1 grid grid-cols-4 gap-4">
						{[
							{ label: "Batch ID", value: batchInfo.id, icon: "tag" },
							{
								label: t(
									"postAutoclaveProcess.physicalInspection.mediaName",
								),
								value: batchInfo.mediaName,
								icon: "science",
							},
							{
								label: t(
									"postAutoclaveProcess.physicalInspection.activity",
								),
								value: batchInfo.activityName,
								icon: "assignment",
							},
							{
								label: t(
									"postAutoclaveProcess.physicalInspection.totalPlates",
								),
								value: batchInfo.totalPlates,
								icon: "inventory_2",
							},
						].map((item, idx) => (
							<div
								key={idx}
								className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-2 flex items-center gap-3"
							>
								<div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
									<span
										className={`material-symbols-outlined text-slate-400 text-lg`}
									>
										{item.icon}
									</span>
								</div>
								<div>
									<p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
										{item.label}
									</p>
									<p className="text-xs font-black text-[#1E293B] truncate max-w-[120px]">
										{item.value}
									</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default PIHeader;
